(() => {
  if (customElements.get('ron-access-preview')) return;

  class RonAccessPortal extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.resizeObserver?.disconnect();
      this.controller = new AbortController();
      this.saved = new Set();
      this.activeTab = 'rotation';
      this.member = null;
      this.busy = false;
      this.copy = JSON.parse(this.querySelector('[data-portal-copy]')?.textContent || '{}');
      const { signal } = this.controller;
      this.tabs = Array.from(this.querySelectorAll('[data-portal-tab]'));
      this.panels = Array.from(this.querySelectorAll('[data-portal-panel]'));
      this.form = this.querySelector('[data-preview-preferences]');
      this.tablist = this.tabs[0]?.closest('[role="tablist"]');
      if (!this.tabs.length || !this.form) return;

      this.addEventListener('click', event => this.onClick(event), { signal });
      this.addEventListener('keydown', event => this.onKeydown(event), { signal });
      this.form.addEventListener('submit', event => {
        event.preventDefault();
        this.applyPreferences();
      }, { signal });
      this.form.elements.shoe_system?.addEventListener('change', () => this.updateShoeSizes(), { signal });
      window.addEventListener('resize', () => this.updateOrientation(), { signal });
      window.addEventListener('pagehide', () => this.setState('loading'), { signal });
      window.addEventListener('pageshow', event => {
        if (event.persisted) this.loadMember();
      }, { signal });
      if (this.tablist && 'ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => this.updateOrientation());
        this.resizeObserver.observe(this.tablist);
      }
      try {
        if (!window.RonAccessAPI) throw new Error('ACCESS API unavailable');
        this.api = window.RonAccessAPI.create({ basePath: this.dataset.apiBase || '/apps/ron-access' });
        const returnTo = window.location.pathname + window.location.search;
        this.querySelector('[data-portal-login]').href = window.RonAccessAPI.getLoginUrl(returnTo);
        this.querySelector('[data-portal-enroll]').href = window.RonAccessAPI.withPreview(this.dataset.enrollmentUrl);
        this.loadMember();
      } catch {
        this.setState('error');
      }
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.resizeObserver?.disconnect();
      this.loadSequence = (this.loadSequence || 0) + 1;
      this.announcement = null;
    }

    text(key, variables = {}) {
      return Object.entries(variables).reduce((message, [name, value]) => message.replaceAll(`__${name.toUpperCase()}__`, String(value)), this.copy[key] || '');
    }

    async loadMember() {
      if (this.busy) return;
      if (!this.api) {
        try {
          this.api = window.RonAccessAPI.create({ basePath: this.dataset.apiBase || '/apps/ron-access' });
        } catch {
          this.setState('error');
          return;
        }
      }
      const sequence = (this.loadSequence || 0) + 1;
      this.loadSequence = sequence;
      this.setState('loading');
      this.showNotice('');
      try {
        const result = await this.api.getMember();
        if (!this.isConnected || sequence !== this.loadSequence) return;
        if (!result.member) {
          this.member = null;
          this.setState('nonmember');
          return;
        }
        if (result.member.status !== 'active') {
          this.member = null;
          this.setState('unavailable');
          return;
        }
        this.acceptMember(result.member);
        this.setState('member');
      } catch (error) {
        if (!this.isConnected || sequence !== this.loadSequence) return;
        this.member = null;
        this.setState(error.status === 401 ? 'guest' : 'error');
      }
    }

    setState(state) {
      this.dataset.portalState = state;
      const isMember = state === 'member';
      const gate = this.querySelector('[data-portal-gate]');
      gate.hidden = isMember;
      gate.setAttribute('aria-busy', String(state === 'loading'));
      this.querySelector('[data-member-content]').hidden = !isMember;
      this.querySelectorAll('[data-portal-controls]').forEach(control => { control.hidden = !isMember; });
      this.querySelector('[data-portal-login]').hidden = state !== 'guest';
      this.querySelector('[data-portal-enroll]').hidden = state !== 'nonmember';
      gate.querySelector('[data-portal-reload]').hidden = !['error', 'unavailable'].includes(state);
      if (!isMember) {
        const prefix = ['loading', 'guest', 'nonmember', 'unavailable'].includes(state) ? state : 'error';
        this.querySelector('[data-gate-heading]').textContent = this.text(`${prefix}_heading`);
        this.querySelector('[data-gate-copy]').textContent = this.text(`${prefix}_copy`);
      } else {
        this.setAttribute('data-portal-ready', '');
        this.selectTab(this.activeTab);
        this.updateOrientation();
      }
    }

    acceptMember(member, { restoreForm = true } = {}) {
      this.member = member;
      this.saved = new Set((member.profile.savedProductIds || []).map(id => String(id).replace(/^gid:\/\/shopify\/Product\//, '')));
      if (restoreForm) this.restorePreferenceForm();
      this.renderPreferences();
      this.renderSaved();
      this.querySelector('[data-member-heading]').textContent = this.text('member_heading');
      const enrolledAt = new Date(member.enrolledAt);
      this.querySelector('[data-member-since]').textContent = Number.isNaN(enrolledAt.getTime())
        ? this.text('member_label')
        : this.text('member_since', { date: enrolledAt.toLocaleDateString(document.documentElement.lang || 'en-US', { month: 'short', year: 'numeric' }) });
    }

    onClick(event) {
      if (!(event.target instanceof Element)) return;
      const control = event.target.closest('[data-portal-tab], [data-portal-open], [data-preview-save], [data-portal-reload], [data-preferences-clear]');
      if (!control || control.closest('ron-access-preview') !== this) return;
      event.preventDefault();
      if (control.hasAttribute('data-portal-reload')) {
        this.loadMember();
        return;
      }
      if (!this.member || this.dataset.portalState !== 'member') return;
      if (control.hasAttribute('data-portal-tab')) this.selectTab(control.dataset.portalTab);
      else if (control.hasAttribute('data-portal-open')) this.selectTab(control.dataset.portalOpen, true);
      else if (control.hasAttribute('data-preview-save')) this.toggleSaved(control);
      else if (control.hasAttribute('data-preferences-clear') && !this.busy) this.clearPreferenceForm();
    }

    onKeydown(event) {
      if (!(event.target instanceof Element)) return;
      const tab = event.target.closest('[data-portal-tab]');
      const current = this.tabs.indexOf(tab);
      if (current < 0) return;
      this.updateOrientation();
      let next;
      const vertical = this.orientation === 'vertical';
      const direction = getComputedStyle(this.tablist || this).direction === 'rtl' ? -1 : 1;
      if (event.key === 'Home') next = 0;
      else if (event.key === 'End') next = this.tabs.length - 1;
      else if (vertical && event.key === 'ArrowDown') next = current + 1;
      else if (vertical && event.key === 'ArrowUp') next = current - 1;
      else if (!vertical && event.key === 'ArrowRight') next = current + direction;
      else if (!vertical && event.key === 'ArrowLeft') next = current - direction;
      else return;
      event.preventDefault();
      next = (next + this.tabs.length) % this.tabs.length;
      this.selectTab(this.tabs[next].dataset.portalTab, true);
    }

    updateOrientation() {
      if (this.tabs.length < 2) return;
      const first = this.tabs[0].getBoundingClientRect();
      const second = this.tabs[1].getBoundingClientRect();
      this.orientation = Math.abs(second.top - first.top) > Math.abs(second.left - first.left) ? 'vertical' : 'horizontal';
      this.tablist?.setAttribute('aria-orientation', this.orientation);
    }

    selectTab(name, focus = false) {
      const target = this.tabs.find(tab => tab.dataset.portalTab === name) || this.tabs[0];
      if (!target) return;
      this.activeTab = target.dataset.portalTab;
      const targetPanel = target.getAttribute('aria-controls');
      this.tabs.forEach(tab => {
        const selected = tab === target;
        tab.setAttribute('aria-selected', String(selected));
        tab.tabIndex = selected ? 0 : -1;
      });
      this.panels.forEach(panel => { panel.hidden = targetPanel ? panel.id !== targetPanel : panel.dataset.portalPanel !== this.activeTab; });
      if (focus) target.focus();
    }

    setBusy(busy) {
      this.busy = busy;
      this.form.setAttribute('aria-busy', String(busy));
      this.form.querySelectorAll('input, select, button').forEach(control => { control.disabled = busy; });
      this.querySelectorAll('[data-preview-save]').forEach(control => { control.disabled = busy; });
      this.querySelector('[data-preference-submit-label]').textContent = this.text(busy ? 'saving' : 'save_preferences');
    }

    async toggleSaved(button) {
      if (this.busy || !this.member) return;
      const id = button.dataset.productId;
      if (!id) return;
      const savedCards = Array.from(this.querySelectorAll('[data-saved-grid] [data-preview-product]'));
      const index = savedCards.indexOf(button.closest('[data-preview-product]'));
      const nextSaved = new Set(this.saved);
      const saving = !nextSaved.has(id);
      if (saving) nextSaved.add(id);
      else nextSaved.delete(id);
      this.setBusy(true);
      this.showNotice('');
      try {
        const result = await this.api.updatePreferences({ revision: this.member.revision, profile: { savedProductIds: [...nextSaved] } });
        if (!result.member || result.member.status !== 'active') throw new Error('Membership update was not confirmed');
        if (!this.isConnected) return;
        this.acceptMember(result.member, { restoreForm: false });
        this.setBusy(false);
        if (!saving && index >= 0) {
          const nextCard = savedCards.slice(index + 1).find(card => !card.hidden) || savedCards.filter(card => !card.hidden).at(-1);
          (nextCard?.querySelector('[data-preview-save]') || this.querySelector('[data-saved-empty] [data-portal-open]') || this.tabs.find(tab => tab.dataset.portalTab === 'saved'))?.focus();
        }
        this.showNotice(this.text(saving ? 'saved_status' : 'removed_status', { product: button.dataset.productTitle }));
      } catch (error) {
        this.handleSaveError(error);
      } finally {
        if (this.isConnected) this.setBusy(false);
      }
    }

    renderSaved() {
      this.querySelectorAll('[data-preview-save]').forEach(button => {
        const saved = this.saved.has(button.dataset.productId);
        button.setAttribute('aria-pressed', String(saved));
        button.setAttribute('aria-label', this.text(saved ? 'remove_label' : 'save_label', { product: button.dataset.productTitle }));
      });
      this.querySelectorAll('[data-saved-count]').forEach(count => { count.textContent = String(this.saved.size); });
      let visible = 0;
      this.querySelectorAll('[data-saved-grid] [data-preview-product]').forEach(card => {
        card.hidden = !this.saved.has(card.dataset.productId);
        if (!card.hidden) visible += 1;
      });
      this.querySelectorAll('[data-saved-empty]').forEach(empty => { empty.hidden = visible > 0; });
      this.querySelectorAll('[data-saved-grid]').forEach(grid => { grid.hidden = visible === 0; });
      const outside = Math.max(0, this.saved.size - visible);
      const note = this.querySelector('[data-saved-outside]');
      note.hidden = outside === 0;
      note.textContent = this.text('saved_outside', { count: outside });
    }

    async applyPreferences() {
      if (this.busy || !this.member || !this.form.reportValidity()) return;
      const data = new FormData(this.form);
      const shoeSize = String(data.get('shoe_size') || '');
      const shoeSizingSystem = String(data.get('shoe_system') || '');
      const phone = String(data.get('phone') || '').replace(/[\s().-]/g, '');
      const smsSubscribed = data.has('sms_consent');
      if (shoeSize && !shoeSizingSystem) {
        this.showFormError(this.text('shoe_system_required'));
        this.form.elements.shoe_system.focus();
        return;
      }
      if ((smsSubscribed && !phone) || (phone && !/^\+[1-9]\d{7,14}$/.test(phone))) {
        this.showFormError(this.text('phone_invalid'));
        this.form.elements.phone.focus();
        return;
      }
      const payload = {
        revision: this.member.revision,
        profile: {
          name: String(data.get('name') || '').trim(),
          favoriteBrands: [...new Set(data.getAll('brand').map(value => String(value).trim()).filter(Boolean))],
          clothingSize: String(data.get('apparel_size') || ''),
          shoeSize,
          shoeSizingSystem
        },
        consents: { email: { subscribed: data.has('email_consent') }, sms: { subscribed: smsSubscribed, phone } }
      };
      this.showFormError('');
      this.showNotice('');
      this.setBusy(true);
      try {
        const result = await this.api.updatePreferences(payload);
        if (!result.member || result.member.status !== 'active') throw new Error('Membership update was not confirmed');
        if (!this.isConnected) return;
        this.acceptMember(result.member);
        this.selectTab('rotation', true);
        this.showNotice(this.text('preferences_saved'));
      } catch (error) {
        this.handleSaveError(error);
      } finally {
        if (this.isConnected) this.setBusy(false);
      }
    }

    handleSaveError(error) {
      if (!this.isConnected) return;
      if (error.status === 401) {
        this.member = null;
        this.saved.clear();
        this.form.reset();
        this.setState('guest');
        return;
      }
      const conflict = error.status === 409;
      const message = this.text(conflict ? 'conflict' : 'save_failed');
      this.showNotice(message, true);
      this.showFormError(message);
    }

    clearPreferenceForm() {
      this.form.querySelectorAll('input[name="brand"]').forEach(input => { input.checked = false; });
      ['apparel_size', 'shoe_size', 'shoe_system'].forEach(name => { this.form.elements[name].value = ''; });
      this.updateShoeSizes();
      this.announce(this.text('choices_cleared'));
    }

    restorePreferenceForm() {
      const profile = this.member.profile;
      this.form.elements.name.value = profile.name || '';
      this.form.elements.phone.value = this.member.consents?.sms?.phone || '';
      this.form.elements.email_consent.checked = Boolean(this.member.consents?.email?.subscribed);
      this.form.elements.sms_consent.checked = Boolean(this.member.consents?.sms?.subscribed);
      const brands = profile.favoriteBrands || [];
      const options = this.form.querySelector('.rap-brand-options');
      brands.forEach(brand => {
        if (Array.from(options.querySelectorAll('input[name="brand"]')).some(input => input.value === brand)) return;
        const label = document.createElement('label');
        const input = document.createElement('input');
        input.type = 'checkbox';
        input.name = 'brand';
        input.value = brand;
        const span = document.createElement('span');
        span.textContent = brand;
        label.append(input, span);
        options.append(label);
      });
      options.querySelectorAll('input[name="brand"]').forEach(input => { input.checked = brands.includes(input.value); });
      this.restoreSelect('apparel_size', profile.clothingSize || '');
      this.restoreSelect('shoe_system', profile.shoeSizingSystem || '');
      this.updateShoeSizes(profile.shoeSize || '');
      this.showFormError('');
    }

    restoreSelect(name, value) {
      const select = this.form.elements[name];
      if (value && !Array.from(select.options).some(option => option.value === value)) {
        select.add(new Option(value, value));
      }
      select.value = value;
    }

    updateShoeSizes(savedValue) {
      const select = this.form.elements.shoe_size;
      const system = this.form.elements.shoe_system.value;
      const previousValue = savedValue === undefined ? select.value : savedValue;
      const start = system === 'EU' ? 32 : 3;
      const end = system === 'EU' ? 50 : 18;
      const options = [new Option(this.text('not_selected'), '')];
      for (let size = start; size <= end; size += 0.5) options.push(new Option(String(size), String(size)));
      select.replaceChildren(...options);
      if (savedValue !== undefined) this.restoreSelect('shoe_size', previousValue);
      else select.value = Array.from(select.options).some(option => option.value === previousValue) ? previousValue : '';
    }

    renderPreferences() {
      const profile = this.member.profile;
      const chips = [...(profile.favoriteBrands || [])];
      if (profile.clothingSize) chips.push(`${this.dataset.apparelLabel} / ${profile.clothingSize}`);
      if (profile.shoeSize) {
        const system = Array.from(this.form.elements.shoe_system.options).find(option => option.value === profile.shoeSizingSystem)?.textContent || profile.shoeSizingSystem;
        chips.push(`${this.dataset.shoeLabel} / ${system ? system + ' ' : ''}${profile.shoeSize}`);
      }
      if (!chips.length) chips.push(this.dataset.emptyPreferences);
      this.querySelectorAll('[data-preference-chips]').forEach(container => {
        const fragment = document.createDocumentFragment();
        chips.forEach(label => {
          const chip = document.createElement('span');
          chip.textContent = label;
          fragment.append(chip);
        });
        container.replaceChildren(fragment);
      });
      const brands = new Set((profile.favoriteBrands || []).map(brand => brand.toLocaleLowerCase()));
      let visible = 0;
      this.querySelectorAll('[data-rotation-grid] [data-preview-product]').forEach(card => {
        card.hidden = brands.size > 0 && !brands.has((card.dataset.vendor || '').trim().toLocaleLowerCase());
        if (!card.hidden) visible += 1;
      });
      this.querySelectorAll('[data-rotation-empty]').forEach(empty => { empty.hidden = visible > 0; });
      this.querySelectorAll('[data-rotation-grid]').forEach(grid => { grid.hidden = visible === 0; });
    }

    showNotice(message, retry = false) {
      this.querySelector('[data-portal-notice]').hidden = !message;
      this.querySelector('[data-portal-message]').textContent = message;
      this.querySelector('[data-portal-notice] [data-portal-reload]').hidden = !retry;
      this.announce(message);
    }

    showFormError(message) {
      const error = this.querySelector('[data-preference-error]');
      error.hidden = !message;
      error.textContent = message;
    }

    announce(message) {
      const status = this.querySelector('[data-preview-status]');
      if (!status) return;
      const announcement = {};
      this.announcement = announcement;
      status.textContent = '';
      queueMicrotask(() => {
        if (this.isConnected && this.announcement === announcement) status.textContent = message;
      });
    }
  }

  customElements.define('ron-access-preview', RonAccessPortal);
})();
